import { SupportedCurrency } from '../types';

export const CURRENCY_RATES: Record<SupportedCurrency, number> = {
  ARS: 7.499,
  MXN: 0.092,
};

export const RECHARGE_OVERRIDES: Record<string, Record<SupportedCurrency, number>> = {
  recharge_vbucks_12500: { ARS: 89999, MXN: 1099 },
  recharge_crew: { ARS: 5500, MXN: 80 },
  crew: { ARS: 5500, MXN: 80 },
};

let _hasPurchasedCrewGlobal = false;

export function setHasPurchasedCrewGlobal(val: boolean): void {
  _hasPurchasedCrewGlobal = val;
}

export function getHasPurchasedCrewGlobal(): boolean {
  return _hasPurchasedCrewGlobal;
}

/**
 * Returns the current Crew price according to first purchase vs recurring status.
 */
export function getCrewPrice(currency: SupportedCurrency = 'ARS', hasPurchased = _hasPurchasedCrewGlobal): number {
  const safe = sanitizeCurrency(currency);
  if (safe === 'MXN') {
    return hasPurchased ? 110 : 80;
  }
  return hasPurchased ? 7999 : 5500;
}

/**
 * Normalizes and sanitizes any currency input.
 * - Trims whitespace and normalizes case (e.g., 'ars' -> 'ARS', 'mxn' -> 'MXN')
 * - Strictly converts any invalid, null, undefined, or legacy 'USD' values to 'ARS'
 * - Guarantees return value is always a valid SupportedCurrency ('ARS' | 'MXN')
 */
export function sanitizeCurrency(input: unknown): SupportedCurrency {
  if (typeof input !== 'string') {
    return 'ARS';
  }
  const normalized = input.trim().toUpperCase();
  if (normalized === 'MXN') {
    return 'MXN';
  }
  return 'ARS';
}

/**
 * Calculates the fiat price for cosmetics or recharge offers.
 * - Handles override IDs safely and falls back to standard calculation if override is not found
 * - Protects against NaN, null, undefined, or infinite vbucks inputs (defaults non-finite to 0)
 * - Guarantees return value is always a finite number (never undefined or NaN)
 */
export function calculateFiatPrice(
  vbucks: number,
  currency: SupportedCurrency = 'ARS',
  offerId?: string
): number {
  const safeCurrency = sanitizeCurrency(currency);
  const safeVbucks = typeof vbucks === 'number' && Number.isFinite(vbucks) ? vbucks : 0;

  if (offerId) {
    const lower = offerId.toLowerCase();
    if (lower === 'recharge_crew' || lower === 'crew' || lower.includes('crew')) {
      return getCrewPrice(safeCurrency);
    }

    if (offerId in RECHARGE_OVERRIDES) {
      const override = RECHARGE_OVERRIDES[offerId];
      const overridePrice = override?.[safeCurrency];
      if (typeof overridePrice === 'number' && Number.isFinite(overridePrice)) {
        return overridePrice;
      }
    }
  }

  const rate = CURRENCY_RATES[safeCurrency] ?? CURRENCY_RATES.ARS;
  return Math.round(safeVbucks * rate);
}

/**
 * Formats a fiat amount for display.
 * - Sanitizes currency to guarantee only 'ARS' or 'MXN' is rendered (never 'USD')
 * - Protects against NaN, null, undefined, or infinite numbers (defaults to 0)
 * - Uses 'es-AR' locale formatting for thousand separators (e.g., "$7.499 ARS")
 */
export function formatPrice(amount: number, currency: SupportedCurrency = 'ARS'): string {
  const safeCurrency = sanitizeCurrency(currency);
  const safeAmount = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  return `$${safeAmount.toLocaleString('es-AR')} ${safeCurrency}`;
}

